import { plural } from "../plural";

/** English strings for the app's shell area. Keys are camelCase, grouped by screen. */
export default {
  app: {
    redirectingToDocs: "Redirecting to docs…",
  },
  /** Relative times on project cards ("Edited 5 minutes ago"). */
  time: {
    edited: "Edited {time}",
    minutesAgo: plural({ one: "{n} minute ago", other: "{n} minutes ago" }),
    hoursAgo: plural({ one: "{n} hour ago", other: "{n} hours ago" }),
    daysAgo: plural({ one: "{n} day ago", other: "{n} days ago" }),
  },
  /** The credits card shown in the sidebar, the account menu and the project menu. */
  credits: {
    title: "Credits",
    left: "{credits} left",
    count: "{credits} credits",
    dailyReset: "Daily credits reset at midnight UTC",
  },
  sidebar: {
    home: "Mythex home",
    collapse: "Collapse sidebar",
    workspace: "Workspace",
    proPlan: "Pro Plan",
    freePlan: "Free Plan",
    members: plural({ one: "{n} member", other: "{n} members" }),
    inviteMembers: "Invite members",
    newWorkspace: "New workspace",
    turnPro: "Turn Pro",
    newProject: "New project",
    projects: "Projects",
    connectors: "Connectors",
    domains: "Domains",
    plansAndCredits: "Plans & credits",
    percentOff: "{percent}% off",
    unlockPro: "unlock Pro features",
    newChat: "New chat",
  },
  recents: {
    title: "Recents",
    organize: "Organize chats",
    inOneList: "In one list",
    byProject: "By project",
    empty: "Your chats will show up here.",
    titleWithProject: "{title} — {project}",
  },
  palette: {
    recentProjects: "Recent projects",
    navigateTo: "Navigate to",
    dashboard: "Dashboard",
    createProject: "Create new project",
    homepage: "Homepage",
    pricing: "Pricing",
  },
  pageSheet: {
    label: "Page",
  },
  account: {
    label: "Account",
    menu: "Account menu",
    menuNewReply: "Account menu — new Support reply",
    settings: "Account settings",
    admin: "Admin",
    support: "Support",
    newReply: "New reply",
    docs: "Docs",
    homepage: "Homepage",
    signOut: "Sign out",
    workspaces: "Workspaces",
  },
  navigator: {
    pin: "Pin sidebar open",
    unpin: "Unpin sidebar",
    projects: "Projects",
    workspaceMembers: "Workspace members",
    newProject: "New project",
    noProjects: "No projects yet.",
    projectOptions: "Project options",
    workspaces: "Workspaces",
    newWorkspace: "New workspace",
    projectsCount: plural({ one: "{n} project", other: "{n} projects" }),
    projectLimit: "Your plan allows {limit} projects. Upgrade to Pro for more.",
    createProjectFailed: "Couldn't create the project. Try again.",
  },
  newWorkspace: {
    title: "New workspace",
    subtitle: "A shared space for your team's projects.",
    namePlaceholder: "Workspace name",
    created: "Workspace \"{name}\" created.",
    failed: "Couldn't create the workspace.",
  },
  members: {
    title: "Members",
    subtitle: "People with access to {workspace}.",
    invite: "Invite",
    invited: "Invitation sent to {email}.",
    alreadyMember: "That person is already a member.",
    inviteFailed: "Couldn't send the invitation.",
    removed: "Member removed.",
    removeFailed: "Couldn't remove that member.",
    owner: "Owner",
    member: "Member",
    removeMember: "Remove member",
    pending: "Pending invitations",
    invitedOn: "Invited {date}",
    revoke: "Revoke",
    removeTitle: "Remove member?",
    removeBody: "“{name}” will lose access to this workspace.",
    revokeTitle: "Revoke invitation?",
    revokeBody: "The invite to {email} will no longer work.",
  },
  dashboard: {
    openMenu: "Open menu",
    openSidebar: "Open sidebar",
    greeting: "Ready to build, {name}?",
    greetingFallbackName: "there",
    typewriterPrefix: "Ask Mythex to create ",
    typewriterWords: [
      "a landing page for my…",
      "a blog about…",
      "a web app that…",
      "a prototype…",
      "an internal tool that…",
      "a dashboard to…",
    ],
    clearSearch: "Clear search",
    browseAll: "Browse all →",
    noProjects: "No projects yet — describe one above.",
    noMatches: "No projects match that search.",
  },
  library: {
    title: "Projects",
    subtitle: "All projects in this workspace.",
    searchPlaceholder: "Search projects…",
    layout: "Project layout",
    gridView: "Grid view",
    listView: "List view",
    noProjects: "No projects yet.",
  },
  projectManage: {
    renameTitle: "Rename project",
    renamed: "Project renamed.",
    renameFailed: "Couldn't rename the project.",
    deleteTitle: "Delete project?",
    deleteBody: "\"{name}\" and its workspace will be permanently removed. Any published app is taken offline.",
    deleted: "Project deleted.",
    deleteFailed: "Couldn't delete the project.",
    options: "Project options",
  },
  projectMenu: {
    label: "Project menu",
    publish: "Publish",
    publishing: "Publishing…",
    newChat: "New chat",
    chatHistory: "Chat history",
    viewApp: "View app",
    viewCode: "View code",
    refreshApp: "Refresh app",
    knowledge: "Knowledge",
    secrets: "Secrets",
    export: "Export",
  },
  errorBoundary: {
    title: "Something went wrong",
    body: "The app hit an unexpected error. Reloading usually fixes it — your work is saved.",
    reload: "Reload",
  },
  theme: {
    toLight: "Switch to light mode",
    toDark: "Switch to dark mode",
  },
  live: "Live",
  upgrade: {
    outOfCredits: "You're out of credits",
    todayUsed: "Today's credits are used up",
    monthUsed: "Monthly free credits used up",
    outOfCreditsTrial:
      "Free is 5 credits/day (up to 30/mo). Start a {days}-day Pro trial for more credits, code edit/export, and a custom domain.",
    outOfCreditsStore:
      "Free is 5 credits/day (up to 30/mo). Upgrade to Pro for more credits, code edit/export, and a custom domain.",
    outOfCreditsSale:
      "Free is 5 credits/day (up to 30/mo). Claim {percent}% off Pro for more credits, code edit/export, and a custom domain.",
    tryFree: "Try Pro free for {days} days",
    getOff: "Get {percent}% off Pro",
    trialBody: "Card required. Cancel anytime before the trial ends — then $25/mo at 30% off.",
    saleBody:
      "Start with Pro — ${price}/mo instead of ${compareAt}. More credits, code edit/export, GitHub, and a custom domain.",
    proDescription: "Designed for builders shipping real products — more credits, code edit/export, and a custom domain.",
    perMonth: "/ month",
    freeDomain: "Free custom domain",
    checkoutFailed: "Couldn't open checkout. Try again.",
    availableOnPro: "{feature} is available on Pro.",
  },
  notify: {
    title: "Get notified when Mythex finishes",
    asking: "Asking…",
    enabled: "We'll let you know when it's done.",
    blocked: "Notifications are blocked for this site. Your browser's site settings can undo that.",
    failed: "Couldn't turn on notifications. You can try again from account settings.",
  },
  notifications: {
    on: "On",
    off: "Off",
    blocked: "Blocked",
    unsupported: "Not available on this device",
    turnOn: "Turn on",
    openSettings: "Open settings",
    iosHint: "iOS only asks once. Turn notifications on for Mythex in Settings.",
    androidHint: "Android stopped asking after you declined. Turn notifications on for Mythex in Settings.",
    webHint:
      "Notifications are blocked for this site. Allow them in your browser's site settings for this page, then reload.",
  },
};
